import React, { useEffect, useRef, useState } from 'react';

import cls from 'classnames';

import _isFunction from 'lodash/isFunction';
import _isNumber from 'lodash/isNumber';
import _times from 'lodash/times';

import {
  ActivityCompactCard,
  ActivityCompactCardSkeleton,
  ActivitiesLikeProps,
  formatNumber,
  LinkWrapper,
  FirstLikeModal,
} from '@ott/activity-card';
import ChevronLeftIcon from '@ott/icon-chevron-left';
import ChevronRightIcon from '@ott/icon-chevron-right';
import ChevronRightIconSmall from '@ott/icon-small-chevron-right';
import { formatMessage } from '@ott/l10n';
import { useLayoutContext } from '@ott/layout-context';

import { ACTIVITIES_TEMPLATE, WidgetProps } from '../../ActivitiesWidget';
import { SearchResult } from '../../effects/useActivitiesSearch';
import usePrevious from '../../effects/usePrevious';
import trackKm from '../../helpers/trackKm';
import ActivitiesIcon from '../../icons/ActivitiesIcon';

import { SliderInstance, useKeenSlider } from 'keen-slider/react';

import styles from './WidgetSlider.scss';

const keenSliderClassName = styles['keen-slider'];
const keenSlideClassName = styles['keen-slider__slide'];

const MOBILE_SLIDES_PER_VIEW = 1;
const DESKTOP_SLIDES_PER_VIEW = 3;

type Props = Pick<
  WidgetProps,
  | 'isSearchCardVisible'
  | 'openAuth'
  | 'shouldOpenInNewTab'
  | 'shouldShowLike'
  | 'shouldUseExternalLinks'
  | 'trackId'
  | 'trackPage'
> &
  Pick<SearchResult, 'isLoading' | 'meta' | 'activities' | 'updateActivity'> & {
    prepareSearchLink: () => string;
    prepareActivityLink: (activityId: number) => string;
  };

const WidgetSlider = ({
  activities,
  isLoading,
  isSearchCardVisible,
  meta,
  openAuth,
  prepareActivityLink,
  prepareSearchLink,
  shouldOpenInNewTab,
  shouldShowLike,
  shouldUseExternalLinks,
  trackId,
  trackPage,
  updateActivity,
}: Props) => {
  const { localityCount, localityName, localityId } = meta;

  const { isMobileLayout } = useLayoutContext();

  const [hadSuccessfulLike, setHadSuccessfulLike] = useState(false);
  const [hasNextSlides, setHasNextSlides] = useState(false);
  const [hasPrevSlides, setHasPrevSlides] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slidesCount, setSlidesCount] = useState(0);
  const [scrollHappenedAtLeastOnce, setScrollHappenedAtLeastOnce] = useState(false);

  const prevSlide = usePrevious(currentSlide);

  const slidesPerView = isMobileLayout ? MOBILE_SLIDES_PER_VIEW : DESKTOP_SLIDES_PER_VIEW;

  useEffect(() => {
    if (_isNumber(prevSlide) && currentSlide !== prevSlide) {
      trackKm.onScroll({
        count: activities.length,
        id: trackId,
        localityId,
        page: trackPage,
        scrollOrder: currentSlide + 1,
      });

      if (!scrollHappenedAtLeastOnce) {
        setScrollHappenedAtLeastOnce(true);
      }
    }
  }, [prevSlide, currentSlide]);

  useEffect(() => {
    if (isSearchCardVisible && slidesCount && currentSlide >= slidesCount - slidesPerView) {
      trackKm.onSearchCardShow({
        id: trackId,
        localityId,
        page: trackPage,
      });
    }
  }, [slidesCount, currentSlide, isSearchCardVisible]);

  const handleChangeOrCreate = (slider: SliderInstance) => {
    const updatedSlideIndex = slider.track.details?.abs;
    const sliderSlidesCount = slider.track.details?.slides?.length;
    // @ts-ignore
    const sliderSlidesPerView = slider.options.slides.perView;

    setHasNextSlides(updatedSlideIndex < sliderSlidesCount - sliderSlidesPerView);
    setHasPrevSlides(updatedSlideIndex !== 0);
    setCurrentSlide(updatedSlideIndex);
    setSlidesCount(sliderSlidesCount);
  };

  const [sliderRef, keenSlider] = useKeenSlider({
    drag: true,
    selector: `.${keenSlideClassName}`,
    slides: {
      perView: DESKTOP_SLIDES_PER_VIEW,
      spacing: 16,
    },
    slideChanged: handleChangeOrCreate,
    created: handleChangeOrCreate,
    breakpoints: {
      '(max-width: 767px)': {
        slides: {
          // @ts-ignore
          perView: MOBILE_SLIDES_PER_VIEW,
          spacing: 16,
        },
      },
    },
  });

  const renderArrows = () => {
    if (!keenSlider || isMobileLayout) {
      return null;
    }

    return (
      <>
        {hasPrevSlides && (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
          <div
            className={cls(styles.arrow, styles.leftArrow)}
            onClick={keenSlider.current?.prev}
            data-locator="left"
          >
            <ChevronLeftIcon />
          </div>
        )}

        {hasNextSlides && (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
          <div
            className={cls(styles.arrow, styles.rightArrow)}
            onClick={keenSlider.current?.next}
            data-locator="right"
          >
            <ChevronRightIcon />
          </div>
        )}
      </>
    );
  };

  const renderDots = () => {
    if (!keenSlider || !isMobileLayout || slidesCount === 1) {
      return null;
    }

    return (
      <div className={styles.dots}>
        {_times(slidesCount, (i) => {
          return (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
            <div
              className={cls(styles.dot, {
                [styles.dotIsActive]: i === currentSlide,
              })}
              onClick={() => keenSlider.current?.moveToIdx(i)}
              key={i}
            />
          );
        })}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className={styles.skeletonsContainer}>
        {_times(slidesPerView, (i) => (
          <ActivityCompactCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  const trackActivityCardClick = (activityId: number, isFavorite: boolean, slideIndex: number) => {
    trackKm.onActivityCardClick({
      afterScrolling: scrollHappenedAtLeastOnce,
      id: trackId,
      localityId,
      offerId: activityId,
      page: trackPage,
      selectionOrder: slideIndex + 1,
      isFavorite,
    });
  };

  const handleLikeUpdateSuccess: ActivitiesLikeProps['onUpdateSuccess'] = ({
    activityId,
    isLiked,
  }) => {
    updateActivity(activityId, {
      key: 'favorite',
      value: isLiked,
    });

    if (isLiked) {
      setHadSuccessfulLike(true);
    }
  };

  const slides = activities.map((activity, i) => {
    const trackData = {
      id: trackId,
      page: trackPage,
      resultsCount: meta.searchCount,
      cardOrder: i + 1,
    };

    const handleClick = () => trackActivityCardClick(activity.activityId, activity.favorite, i);

    return (
      <div className={cls(keenSlideClassName, styles.slide)} key={activity.activityId}>
        <ActivityCompactCard
          activity={activity}
          isExternalLink={shouldUseExternalLinks}
          onClick={handleClick}
          openInNewTab={shouldOpenInNewTab}
          prepareActivityLink={prepareActivityLink}
          shouldShowLike={shouldShowLike}
          onLikeUpdateSuccess={handleLikeUpdateSuccess}
          trackData={trackData}
        />
      </div>
    );
  });

  const trackSearchCardClick = () => {
    trackKm.onSearchCardClick({
      id: trackId,
      localityId,
      page: trackPage,
    });
  };

  if (isSearchCardVisible) {
    const activitiesLeft = localityCount - slides.length;

    if (activitiesLeft > 0) {
      slides.push(
        <div className={keenSlideClassName} key="search-card">
          <LinkWrapper
            className={styles.searchCard}
            isExternalLink={shouldUseExternalLinks}
            openInNewTab={shouldOpenInNewTab}
            specificLocators="search-card"
            to={prepareSearchLink()}
            onClick={() => trackSearchCardClick()}
          >
            <ActivitiesIcon className={styles.searchCardBackIcon} />
            <div className={styles.searchCardTitle}>
              {`Ещё ${formatNumber(activitiesLeft)} ${formatMessage(
                {
                  id: 'activitiesWidget.activities',
                  defaultMessage: ACTIVITIES_TEMPLATE,
                },
                {
                  count: activitiesLeft,
                }
              )}`}{' '}
              {Boolean(localityName) && <div>в городе {localityName}</div>}
            </div>
            <div className={styles.searchCardAll}>
              <span>Смотреть все</span>
              <ChevronRightIconSmall className={styles.searchCardIcon} />
            </div>
          </LinkWrapper>
        </div>
      );
    }
  }

  return (
    <div className={styles.container} data-locator="slider">
      <div className={keenSliderClassName} ref={sliderRef}>
        {slides}
      </div>

      {renderArrows()}
      {renderDots()}

      {_isFunction(openAuth) && (
        <FirstLikeModal
          hadSuccessfulLike={hadSuccessfulLike}
          openAuth={openAuth}
          trackData={{
            page: trackPage,
            localityId,
          }}
        />
      )}
    </div>
  );
};

export default WidgetSlider;
